import { reactive, ref, computed, getCurrentInstance } from 'vue'
import { useRouter } from 'vue-router'

export function useLoginLogic() {
    const router = useRouter()
    const instance = getCurrentInstance()
    const { $request,  $ValidConst } = instance.appContext.config.globalProperties

    // 登录方式：userPwd-密码登录，code-验证码登录
    const loginType = ref('userPwd')

    // 表单数据 - 分开存储手机号和邮箱
    const form = reactive({
        phoneNumber: '',  // 手机号（仅密码登录使用）
        email: '',        // 邮箱（仅验证码登录使用）
        userPwd: '',      // 密码
        code: ''          // 验证码
    })

    // 输入框引用
    const phoneNumberInput = ref(null)
    const emailInput = ref(null)
    const userPwdInput = ref(null)
    const codeInput = ref(null)

    // 错误状态
    const fieldError = reactive({
        phoneNumber: false,
        email: false,
        userPwd: false,
        code: false
    })

    // 错误提示文本
    const errorText = reactive({
        phoneNumber: '',
        email: '',
        userPwd: '',
        code: ''
    })

    // 验证码倒计时相关
    const isCountingDown = ref(false)
    const countDown = ref(60)
    let countDownTimer = null

    // 全局状态
    const globalError = ref('')
    const isLoading = ref(false)
    // 用于触发抖动动画的标志
    const shakeTrigger = ref(0)

    // 邮箱验证状态计算属性
    const isEmailValid = computed(() => {
        return !fieldError.email && form.email.trim() !== '' && $ValidConst.EMAIL_REG.test(form.email.trim())
    })


    // 所有字段有效（控制提交状态）
    const isAllValid = computed(() => {
        if (loginType.value === 'userPwd') {
            return !fieldError.phoneNumber && !fieldError.userPwd &&
                form.phoneNumber.trim() && form.userPwd.trim()
        } else {
            return !fieldError.email && !fieldError.code &&
                form.email.trim() && form.code.trim()
        }
    })

    // 输入处理（清空错误）
    const handleInput = (field) => {
        fieldError[field] = false
        errorText[field] = ''
        globalError.value = ''

        // 清空原生验证提示
        const inputEl = {
            phoneNumber: phoneNumberInput.value,
            email: emailInput.value,
            userPwd: userPwdInput.value,
            code: codeInput.value
        }[field]
        if (inputEl) {
            inputEl.setCustomValidity('')
        }
    }

    // 字段校验
    const checkField = (field) => {
        // 手机号验证
        if (field === 'phoneNumber') {
            if (form.phoneNumber.trim() === '') {
                errorText.phoneNumber = '请输入手机号'
                fieldError.phoneNumber = true
                return
            }

            if (!$ValidConst.PHONE_REG.test(form.phoneNumber.trim())) {
                errorText.phoneNumber = $ValidConst.PHONE_MSG
                fieldError.phoneNumber = true
                return
            }

            fieldError.phoneNumber = false
            errorText.phoneNumber = ''
        }

        // 邮箱验证
        if (field === 'email') {
            if (form.email.trim() === '') {
                errorText.email = '请输入邮箱'
                fieldError.email = true
                return
            }

            if (!$ValidConst.EMAIL_REG.test(form.email.trim())) {
                errorText.email = $ValidConst.EMAIL_MSG
                fieldError.email = true
                return
            }

            fieldError.email = false
            errorText.email = ''
        }

        // 密码验证
        if (field === 'userPwd') {
            if (form.userPwd.trim() === '') {
                errorText.userPwd = '请输入密码'
                fieldError.userPwd = true
                return
            }
            if (!$ValidConst.PWD_REG.test(form.userPwd.trim())) {
                errorText.userPwd = $ValidConst.PWD_MSG
                fieldError.userPwd = true
                return
            }
            fieldError.userPwd = false
            errorText.userPwd = ''
        }

        // 验证码验证
        if (field === 'code') {
            if (form.code.trim() === '') {
                errorText.code = '请输入验证码'
                fieldError.code = true
                return
            }
            if (!$ValidConst.CODE_REG.test(form.code.trim())) {
                errorText.code = $ValidConst.CODE_MSG
                fieldError.code = true
                return
            }
            fieldError.code = false
            errorText.code = ''
        }
    }

    // 失去焦点校验
    const validateField = (field) => {
        // 仅在提交后才校验空值
        if (!isLoading.value && form[field].trim() === '') {
            return
        }
        checkField(field)
    }

    // 设置全局错误并触发抖动
    const setGlobalError = (message) => {
        // 先清空错误，触发重绘
        globalError.value = ''
        instance.proxy.$nextTick(() => {
            globalError.value = message
            // 每次设置错误时增加触发器值，触发动画
            shakeTrigger.value++
        })
    }

    // 获取验证码
    const getVerificationCode = async () =>  {
        checkField('email')
        if (!isEmailValid.value) return

        try {
            isCountingDown.value = true
            // 请求封装成表单格式
            const formData = new FormData();
            formData.append('email', form.email);
            const res = await $request.post('/login/sendCode', formData);

            if (res && res.code === 200) {
                setGlobalError(res.msg || '验证码已发送，请查收')
                let count = 60
                countDown.value = count

                // 清除可能存在的旧定时器
                if (countDownTimer) clearInterval(countDownTimer)

                countDownTimer = setInterval(() => {
                    count--
                    countDown.value = count
                    if (count <= 0) {
                        clearInterval(countDownTimer)
                        isCountingDown.value = false
                        countDownTimer = null
                    }
                }, 1000)
            } else {
                setGlobalError(res.msg || '发送验证码失败，请重试')
                isCountingDown.value = false
            }
        } catch (err) {
            setGlobalError(err.response?.data?.msg || err.message || '发送验证码失败，请重试')
            isCountingDown.value = false
        }
    }

    // 处理登录
    const handleLogin = async () => {
        // 清除之前的错误提示
        Object.keys(fieldError).forEach(key => {
            fieldError[key] = false
            errorText[key] = ''
        })
        globalError.value = ''

        // 全量校验
        if (loginType.value === 'userPwd') {
            checkField('phoneNumber')
            checkField('userPwd')
        } else {
            checkField('email')
            checkField('code')
        }

        // 检查是否有错误
        if (!isAllValid.value) {
            return
        }

        try {
            isLoading.value = true
            // 构建登录参数
            const params = {
                type: loginType.value
            }

            if (loginType.value === 'userPwd') {
                params.phoneNumber = form.phoneNumber
                params.userPwd = form.userPwd
            } else {
                params.email = form.email
                params.code = form.code
            }

            const res = await $request.post('/login/login', params)

            if (res && res.code === 200) {
                sessionStorage.setItem('token', res.data.token || res.data);
                
                // 如果有存储空间警告，弹窗提示
                if (res.data.storageWarning) {
                    instance.proxy.$modal.warning({
                        title: '存储空间警告',
                        content: res.data.storageWarning,
                        showCancel: false
                    })
                }
                
                // 登录成功跳转首页
                router.push('/home')
            } else {
                setGlobalError(res.msg || '登录失败，请检查账号密码')
            }
        } catch (err) {
            setGlobalError(err.response?.data?.msg || err.message || '登录失败，请稍后重试')
        } finally {
            isLoading.value = false
        }
    }

    // 前往注册页面
    const goToRegister = () => {
        router.push('/register')
    }

    // 前往找回密码页面
    const goToFindPwd = () => {
        router.push('/findPwd')
    }

    // 清理定时器
    const cleanup = () => {
        if (countDownTimer) {
            clearInterval(countDownTimer)
            countDownTimer = null
        }
    }

    // 组件卸载时清理
    window.addEventListener('beforeunload', cleanup)

    return {
        form,
        fieldError,
        errorText,
        globalError,
        shakeTrigger,
        isLoading,
        handleLogin,
        goToRegister,
        goToFindPwd,
        handleInput,
        validateField,
        phoneNumberInput,
        emailInput,
        userPwdInput,
        codeInput,
        loginType,
        isCountingDown,
        countDown,
        getVerificationCode,
        isAllValid,
        isEmailValid
    }
}